import { Navigate } from 'react-router-dom';
import { usePermission } from '@/shared';

interface PermissionRouteProps {
  children: React.ReactNode;
  /** Single required permission, e.g. `product.index`. */
  permission?: string;
  /** Allow the route when the user has any of these permissions. */
  anyOf?: string[];
  fallbackPath?: string; // Where to redirect if no permission (default: '/')
}

/**
 * Permission-based route protection.
 * Redirects to fallbackPath (default: home) if user doesn't have required permission.
 */
const matchesPermission = (permission: string, canAccess: (resource: string, action: string) => boolean) => {
  const [resource, action] = permission.split('.');
  if (!resource) return false;
  return canAccess(resource, action || 'index');
};

export const PermissionRoute = ({
  children,
  permission,
  anyOf,
  fallbackPath = '/',
}: PermissionRouteProps) => {
  const { canAccess } = usePermission();

  const allowed =
    (permission ? matchesPermission(permission, canAccess) : false) ||
    (anyOf?.some((item) => matchesPermission(item, canAccess)) ?? false);

  if (!allowed) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
};
